import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  getOutstandingBalance,
  getOutstandingBalanceOverview,
} from '@services/api/dashboard/finance.api'
import type {
  ARAgingBucket,
  OutstandingBalanceDashboard,
  OutstandingBalancePatientRow,
} from '@services/api/dashboard/finance.api'
import { AR_BUCKET_COLORS } from '@constants/Dashboard/financeDashboard.constants'
import { computeChartAxis } from '@utils/dashboardFinance.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'

export type UseOutstandingBalanceOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseOutstandingBalanceResult = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  hasData: boolean
  totals: OutstandingBalanceDashboard['totals'] | undefined
  patients: OutstandingBalancePatientRow[]
  aging: ReturnType<typeof computeChartAxis> & {
    data: Array<{ label: string; value: number; total: string }>
  }
  agingColors: Record<string, string>
}

function bucketBalance(b: ARAgingBucket): number {
  return b.insuranceAR + b.patientAR
}

function useOutstandingBalanceFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseOutstandingBalanceOptions & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getOutstandingBalance>>
  >
}): UseOutstandingBalanceResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )

  const totals = data?.totals
  const buckets = useMemo(() => data?.buckets ?? [], [data])
  const patients: OutstandingBalancePatientRow[] = useMemo(
    () => data?.patients ?? [],
    [data],
  )

  const aging = useMemo(() => {
    const chartData = buckets.map((b) => ({
      label: b.bucket,
      value: bucketBalance(b) / 1000,
      total: (bucketBalance(b) / 1000).toFixed(2),
    }))
    const axis = computeChartAxis(chartData.map((d) => d.value))
    return { data: chartData, ...axis }
  }, [buckets])

  const agingColors = useMemo(() => {
    const colors: Record<string, string> = {}
    aging.data.forEach((p) => {
      colors[p.label] = AR_BUCKET_COLORS[p.label] ?? 'var(--blue-500)'
    })
    return colors
  }, [aging])

  return {
    loading,
    error,
    hasPayload: data != null,
    hasData: hasNonZeroDashboardValues(
      totals?.totalBalance.current,
      totals?.totalCharges.current,
      totals?.totalPayments.current,
      totals?.accountsWithBalance.current,
    ),
    totals,
    patients,
    aging,
    agingColors,
  }
}

export function useOutstandingBalance(
  options: UseOutstandingBalanceOptions,
): UseOutstandingBalanceResult {
  return useOutstandingBalanceFromFetcher({
    ...options,
    fetcher: getOutstandingBalance,
  })
}

export function useOutstandingBalanceOverview(
  options: UseOutstandingBalanceOptions,
): UseOutstandingBalanceResult {
  return useOutstandingBalanceFromFetcher({
    ...options,
    fetcher: getOutstandingBalanceOverview,
  })
}
